/* eslint-disable react-refresh/only-export-components -- This module exports types/helpers alongside components. */
import React from 'react';
import clsx from 'clsx';
import {
  IconFileTypePdf,
  IconFileTypeDoc,
  IconFileTypeXls,
  IconFileTypePpt,
  IconFileText,
  IconFileZip,
  IconPhoto,
  IconMusic,
  IconVideo,
  IconFileCode,
  IconFile,
  IconFileTypeCsv,
  IconFileTypeJs,
  IconFileTypeHtml,
  IconFileTypeCss,
  IconBrandPython,
  IconFileX,
  IconCheck,
  IconAlertTriangle,
  IconInfoCircle,
} from '@tabler/icons-react';

export type FileFormatIconSize =
  | '2xsmall'
  | 'xsmall'
  | 'small'
  | 'medium'
  | 'large';
export type FileFormatIconColor =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'sky'
  | 'blue'
  | 'purple'
  | 'pink'
  | 'gray'
  | 'error'
  | 'success'
  | 'warning'
  | 'info'
  | 'primary'
  | 'secondary';

interface FileInfo {
  format: string;
  color: FileFormatIconColor;
  icon?: React.ComponentType<{ size?: number | string; className?: string }>;
}

// Mapping MIME types to file information
const MIME_TYPE_MAP: Record<string, FileInfo> = {
  // PDF
  'application/pdf': { format: 'PDF', color: 'red', icon: IconFileTypePdf },

  // Microsoft Office
  'application/msword': { format: 'DOC', color: 'blue', icon: IconFileTypeDoc },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    format: 'DOCX',
    color: 'blue',
    icon: IconFileTypeDoc,
  },
  'application/vnd.ms-excel': {
    format: 'XLS',
    color: 'green',
    icon: IconFileTypeXls,
  },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    format: 'XLSX',
    color: 'green',
    icon: IconFileTypeXls,
  },
  'application/vnd.ms-powerpoint': {
    format: 'PPT',
    color: 'orange',
    icon: IconFileTypePpt,
  },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
    format: 'PPTX',
    color: 'orange',
    icon: IconFileTypePpt,
  },

  // Text files
  'text/plain': { format: 'TXT', color: 'gray', icon: IconFileText },
  'text/rtf': { format: 'RTF', color: 'gray', icon: IconFileText },
  'text/csv': { format: 'CSV', color: 'success', icon: IconFileTypeCsv },

  // Web files
  'text/html': { format: 'HTML', color: 'orange', icon: IconFileTypeHtml },
  'text/css': { format: 'CSS', color: 'blue', icon: IconFileTypeCss },
  'text/javascript': { format: 'JS', color: 'yellow', icon: IconFileTypeJs },
  'application/javascript': {
    format: 'JS',
    color: 'yellow',
    icon: IconFileTypeJs,
  },
  'application/typescript': { format: 'TS', color: 'blue', icon: IconFileCode },
  'application/json': { format: 'JSON', color: 'info', icon: IconFileCode },
  'application/xml': { format: 'XML', color: 'success', icon: IconFileCode },
  'text/xml': { format: 'XML', color: 'success', icon: IconFileCode },

  // Images
  'image/jpeg': { format: 'JPG', color: 'purple', icon: IconPhoto },
  'image/jpg': { format: 'JPG', color: 'purple', icon: IconPhoto },
  'image/png': { format: 'PNG', color: 'purple', icon: IconPhoto },
  'image/gif': { format: 'GIF', color: 'purple', icon: IconPhoto },
  'image/svg+xml': { format: 'SVG', color: 'purple', icon: IconPhoto },
  'image/webp': { format: 'WEBP', color: 'purple', icon: IconPhoto },
  'image/bmp': { format: 'BMP', color: 'purple', icon: IconPhoto },
  'image/tiff': { format: 'TIFF', color: 'purple', icon: IconPhoto },
  'image/ico': { format: 'ICO', color: 'purple', icon: IconPhoto },

  // Audio
  'audio/mpeg': { format: 'MP3', color: 'pink', icon: IconMusic },
  'audio/wav': { format: 'WAV', color: 'pink', icon: IconMusic },
  'audio/ogg': { format: 'OGG', color: 'pink', icon: IconMusic },
  'audio/aac': { format: 'AAC', color: 'pink', icon: IconMusic },
  'audio/flac': { format: 'FLAC', color: 'pink', icon: IconMusic },
  'audio/x-wav': { format: 'WAV', color: 'pink', icon: IconMusic },

  // Video
  'video/mp4': { format: 'MP4', color: 'yellow', icon: IconVideo },
  'video/avi': { format: 'AVI', color: 'yellow', icon: IconVideo },
  'video/quicktime': { format: 'MOV', color: 'yellow', icon: IconVideo },
  'video/x-msvideo': { format: 'AVI', color: 'yellow', icon: IconVideo },
  'video/webm': { format: 'WEBM', color: 'yellow', icon: IconVideo },
  'video/ogg': { format: 'OGV', color: 'yellow', icon: IconVideo },
  'video/x-flv': { format: 'FLV', color: 'yellow', icon: IconVideo },

  // Archives
  'application/zip': { format: 'ZIP', color: 'gray', icon: IconFileZip },
  'application/x-rar-compressed': {
    format: 'RAR',
    color: 'gray',
    icon: IconFileZip,
  },
  'application/x-7z-compressed': {
    format: '7Z',
    color: 'gray',
    icon: IconFileZip,
  },
  'application/x-tar': { format: 'TAR', color: 'gray', icon: IconFileZip },
  'application/gzip': { format: 'GZ', color: 'gray', icon: IconFileZip },
  'application/x-gzip': { format: 'GZ', color: 'gray', icon: IconFileZip },

  // Code files
  'text/x-python': { format: 'PY', color: 'info', icon: IconBrandPython },
  'text/x-java-source': { format: 'JAVA', color: 'orange', icon: IconFileCode },
  'text/x-c': { format: 'C', color: 'gray', icon: IconFileCode },
  'text/x-c++': { format: 'CPP', color: 'gray', icon: IconFileCode },
  'text/x-csharp': { format: 'CS', color: 'blue', icon: IconFileCode },
  'text/x-php': { format: 'PHP', color: 'purple', icon: IconFileCode },
  'text/x-ruby': { format: 'RB', color: 'red', icon: IconFileCode },
  'text/x-go': { format: 'GO', color: 'sky', icon: IconFileCode },
  'text/x-rust': { format: 'RS', color: 'orange', icon: IconFileCode },
  'text/x-swift': { format: 'SWIFT', color: 'orange', icon: IconFileCode },

  // Medical files
  'application/dicom': { format: 'DICOM', color: 'info', icon: IconFileText },

  // Other
  'application/octet-stream': { format: 'BIN', color: 'gray', icon: IconFile },
  'application/x-executable': {
    format: 'EXE',
    color: 'error',
    icon: IconFileX,
  },
  'application/x-deb': { format: 'DEB', color: 'orange', icon: IconFile },
  'application/x-rpm': { format: 'RPM', color: 'red', icon: IconFile },
  'application/vnd.android.package-archive': {
    format: 'APK',
    color: 'green',
    icon: IconFile,
  },
  'application/x-apple-diskimage': {
    format: 'DMG',
    color: 'gray',
    icon: IconFile,
  },

  // Fonts
  'font/ttf': { format: 'TTF', color: 'gray' },
  'font/otf': { format: 'OTF', color: 'gray' },
  'font/woff': { format: 'WOFF', color: 'gray' },
  'font/woff2': { format: 'WOFF2', color: 'gray' },
  'application/font-woff': { format: 'WOFF', color: 'gray' },
  'application/font-woff2': { format: 'WOFF2', color: 'gray' },
};

// Function to get file information by MIME type
export function getFileInfoFromMimeType(mimeType: string): FileInfo | null {
  return MIME_TYPE_MAP[mimeType.toLowerCase()] || null;
}

// Function to get file information by extension (fallback)
export function getFileInfoFromExtension(extension: string): FileInfo | null {
  const ext = extension.toLowerCase().replace('.', '');

  const extensionMap: Record<string, FileInfo> = {
    // Documents
    pdf: { format: 'PDF', color: 'red', icon: IconFileTypePdf },
    doc: { format: 'DOC', color: 'blue', icon: IconFileTypeDoc },
    docx: { format: 'DOCX', color: 'blue', icon: IconFileTypeDoc },
    xls: { format: 'XLS', color: 'green', icon: IconFileTypeXls },
    xlsx: { format: 'XLSX', color: 'green', icon: IconFileTypeXls },
    ppt: { format: 'PPT', color: 'orange', icon: IconFileTypePpt },
    pptx: { format: 'PPTX', color: 'orange', icon: IconFileTypePpt },
    txt: { format: 'TXT', color: 'gray', icon: IconFileText },
    rtf: { format: 'RTF', color: 'gray', icon: IconFileText },
    csv: { format: 'CSV', color: 'success', icon: IconFileTypeCsv },

    // Images
    jpg: { format: 'JPG', color: 'purple', icon: IconPhoto },
    jpeg: { format: 'JPG', color: 'purple', icon: IconPhoto },
    png: { format: 'PNG', color: 'purple', icon: IconPhoto },
    gif: { format: 'GIF', color: 'purple', icon: IconPhoto },
    svg: { format: 'SVG', color: 'purple', icon: IconPhoto },
    webp: { format: 'WEBP', color: 'purple', icon: IconPhoto },
    bmp: { format: 'BMP', color: 'purple', icon: IconPhoto },
    tiff: { format: 'TIFF', color: 'purple', icon: IconPhoto },
    ico: { format: 'ICO', color: 'purple', icon: IconPhoto },

    // Audio
    mp3: { format: 'MP3', color: 'pink', icon: IconMusic },
    wav: { format: 'WAV', color: 'pink', icon: IconMusic },
    ogg: { format: 'OGG', color: 'pink', icon: IconMusic },
    aac: { format: 'AAC', color: 'pink', icon: IconMusic },
    flac: { format: 'FLAC', color: 'pink', icon: IconMusic },

    // Video
    mp4: { format: 'MP4', color: 'yellow', icon: IconVideo },
    avi: { format: 'AVI', color: 'yellow', icon: IconVideo },
    mov: { format: 'MOV', color: 'yellow', icon: IconVideo },
    webm: { format: 'WEBM', color: 'yellow', icon: IconVideo },
    mkv: { format: 'MKV', color: 'yellow', icon: IconVideo },
    flv: { format: 'FLV', color: 'yellow', icon: IconVideo },

    // Archives
    zip: { format: 'ZIP', color: 'gray', icon: IconFileZip },
    rar: { format: 'RAR', color: 'gray', icon: IconFileZip },
    '7z': { format: '7Z', color: 'gray', icon: IconFileZip },
    tar: { format: 'TAR', color: 'gray', icon: IconFileZip },
    gz: { format: 'GZ', color: 'gray', icon: IconFileZip },

    // Code
    html: { format: 'HTML', color: 'orange', icon: IconFileTypeHtml },
    css: { format: 'CSS', color: 'blue', icon: IconFileTypeCss },
    js: { format: 'JS', color: 'yellow', icon: IconFileTypeJs },
    ts: { format: 'TS', color: 'blue', icon: IconFileCode },
    json: { format: 'JSON', color: 'info', icon: IconFileCode },
    xml: { format: 'XML', color: 'success', icon: IconFileCode },
    py: { format: 'PY', color: 'info', icon: IconBrandPython },
    java: { format: 'JAVA', color: 'orange', icon: IconFileCode },
    c: { format: 'C', color: 'gray', icon: IconFileCode },
    cpp: { format: 'CPP', color: 'gray', icon: IconFileCode },
    cs: { format: 'CS', color: 'blue', icon: IconFileCode },
    php: { format: 'PHP', color: 'purple', icon: IconFileCode },
    rb: { format: 'RB', color: 'red', icon: IconFileCode },
    go: { format: 'GO', color: 'sky', icon: IconFileCode },
    rs: { format: 'RS', color: 'orange', icon: IconFileCode },
    swift: { format: 'SWIFT', color: 'orange', icon: IconFileCode },

    // Medical files
    dcm: { format: 'DICOM', color: 'info', icon: IconFileText },
    dicom: { format: 'DICOM', color: 'info', icon: IconFileText },

    // Other
    dmg: { format: 'DMG', color: 'gray', icon: IconFile },
    exe: { format: 'EXE', color: 'error', icon: IconFileX },
    deb: { format: 'DEB', color: 'orange', icon: IconFile },
    rpm: { format: 'RPM', color: 'red', icon: IconFile },
    apk: { format: 'APK', color: 'green', icon: IconFile },
    bin: { format: 'BIN', color: 'gray', icon: IconFile },

    // Fonts
    ttf: { format: 'TTF', color: 'gray', icon: IconFileText },
    otf: { format: 'OTF', color: 'gray', icon: IconFileText },
    woff: { format: 'WOFF', color: 'gray', icon: IconFileText },
    woff2: { format: 'WOFF2', color: 'gray', icon: IconFileText },
  };

  return extensionMap[ext] || null;
}

// Function to get semantic icons for statuses
export function getSemanticIcon(
  status: 'success' | 'error' | 'warning' | 'info'
): React.ComponentType<{ size?: number | string; className?: string }> {
  const semanticMap = {
    success: IconCheck,
    error: IconFileX,
    warning: IconAlertTriangle,
    info: IconInfoCircle,
  };

  return semanticMap[status];
}

interface FileFormatIconProps extends React.SVGProps<SVGSVGElement> {
  // Manual configuration (has priority)
  format?: string;
  size?: FileFormatIconSize;
  color?: FileFormatIconColor;
  icon?: React.ComponentType<{ size?: number | string; className?: string }>;

  // Automatic detection
  mimeType?: string;
  fileName?: string;

  // Display mode
  showIcon?: boolean; // If true, show icon instead of text (when available)

  className?: string;
}

export const FileFormatIcon: React.FC<FileFormatIconProps> = ({
  format: manualFormat,
  size = 'medium',
  color: manualColor,
  icon: manualIcon,
  mimeType,
  fileName,
  showIcon = false,
  className,
  ...rest
}) => {
  // Determine format, color, and icon
  let finalFormat = manualFormat;
  let finalColor = manualColor;
  let finalIcon = manualIcon;

  // If format, color, or icon are not set manually, try to determine automatically
  if (!finalFormat || !finalColor || (!finalIcon && showIcon)) {
    let autoFileInfo: FileInfo | null = null;

    // First try by MIME type
    if (mimeType) {
      autoFileInfo = getFileInfoFromMimeType(mimeType);
    }

    // If not found by MIME type, try by extension from file name
    if (!autoFileInfo && fileName) {
      const extension = fileName.split('.').pop();
      if (extension) {
        autoFileInfo = getFileInfoFromExtension(extension);
      }
    }

    // Apply automatically determined values if not set manually
    if (autoFileInfo) {
      finalFormat = finalFormat || autoFileInfo.format;
      finalColor = finalColor || autoFileInfo.color;
      finalIcon = finalIcon || autoFileInfo.icon;
    }
  }

  // Improved fallback logic: if format is not found but fileName exists, use extension
  if (!finalFormat && fileName) {
    const extension = fileName.split('.').pop();
    if (extension) {
      finalFormat = extension.toUpperCase();
    }
  }

  // Final fallback values
  finalFormat = finalFormat || 'FILE';
  finalColor = finalColor || 'gray';

  const baseClass = 'file-format-icon';
  const sizeClass = `${baseClass}--${size}`;
  const colorClass = `${baseClass}--${finalColor}`;

  return (
    <div className={clsx(baseClass, sizeClass, colorClass, className)}>
      <svg
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`${baseClass}__svg`}
        {...rest}
      >
        <path
          d="M30 39.25H10C7.10051 39.25 4.75 36.8995 4.75 34V6C4.75 3.10051 7.10051 0.75 10 0.75H20.5147C21.9071 0.75 23.2425 1.30312 24.227 2.28769L33.7123 11.773C34.6969 12.7575 35.25 14.0929 35.25 15.4853V34C35.25 36.8995 32.8995 39.25 30 39.25Z"
          className={`${baseClass}__document-body`}
          strokeWidth="1.5"
        />
        <path
          d="M23 1V9C23 11.2091 24.7909 13 27 13H35"
          className={`${baseClass}__document-fold`}
          strokeWidth="1.5"
        />
      </svg>
      <div className={`${baseClass}__format-label`}>
        {showIcon && finalIcon
          ? React.createElement(finalIcon, { className: `${baseClass}__icon` })
          : finalFormat}
      </div>
    </div>
  );
};
